import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { AppHeader } from "@/components/app/AppHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/lib/auth-context";
import { listMyLeaves, type LeaveStatus } from "@/lib/leave";
import { formatIst } from "@/lib/tz";

export const Route = createFileRoute("/_app/leave/my-leaves")({
  component: MyLeavesPage,
  head: () => ({ meta: [{ title: "My Leaves" }] }),
});

const TYPE_LABEL: Record<string, string> = {
  paid_full_day: "Paid (Full Day)",
  paid_half_day: "Paid (Half Day)",
  unpaid: "Unpaid",
};

function MyLeavesPage() {
  const { user } = useAuth();
  const [monthFilter, setMonthFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: leaves = [] } = useQuery({
    queryKey: ["my-leaves", user?.id],
    enabled: !!user,
    queryFn: () => listMyLeaves(user!.id),
  });

  const monthOptions = useMemo(() => {
    const set = new Set<string>();
    leaves.forEach((l) => set.add(l.from_date.slice(0, 7)));
    return Array.from(set).sort().reverse();
  }, [leaves]);

  const filtered = useMemo(() => {
    return leaves.filter((l) => {
      if (monthFilter !== "all" && l.from_date.slice(0, 7) !== monthFilter) return false;
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      return true;
    });
  }, [leaves, monthFilter, statusFilter]);

  const StatusPill = ({ s }: { s: LeaveStatus }) => {
    if (s === "approved") return <Badge className="bg-green-600 hover:bg-green-700">Approved</Badge>;
    if (s === "rejected") return <Badge variant="destructive">Rejected</Badge>;
    return <Badge className="bg-amber-500 hover:bg-amber-600">Pending</Badge>;
  };

  return (
    <>
      <AppHeader subtitle="Your leave applications" />
      <div className="p-6 space-y-4">
        <div className="flex gap-2">
          <Select value={monthFilter} onValueChange={setMonthFilter}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Month" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All months</SelectItem>
              {monthOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Applied On</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">No leave applications</TableCell></TableRow>
                ) : filtered.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="text-xs">{TYPE_LABEL[l.leave_type] ?? l.leave_type}</TableCell>
                    <TableCell>{formatIst(l.from_date, "MMM d, yyyy")}</TableCell>
                    <TableCell>{formatIst(l.to_date, "MMM d, yyyy")}</TableCell>
                    <TableCell>{Number(l.total_days).toFixed(1)}</TableCell>
                    <TableCell className="max-w-[260px] truncate text-xs">{l.reason}</TableCell>
                    <TableCell className="text-xs">{formatIst(l.created_at, "MMM d")}</TableCell>
                    <TableCell><StatusPill s={l.status} /></TableCell>
                    <TableCell className="text-xs cursor-default">
                      {l.status === "rejected" ? (() => {
                        const note = l.rejection_note?.trim();
                        const truncated = note && note.length > 40 ? note.slice(0, 40) + "…" : note;
                        return (
                          <div className="flex items-center gap-2">
                            {note ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="cursor-default">{truncated}</span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">{note}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <span className="italic text-muted-foreground">No reason provided</span>
                            )}
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="underline text-primary cursor-pointer">View</button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80">
                                <div className="space-y-2">
                                  <div className="font-semibold text-destructive">✗ Leave Rejected</div>
                                  <div>
                                    <div className="text-xs text-muted-foreground">HR Note:</div>
                                    <div className="italic">"{note || "No reason provided"}"</div>
                                  </div>
                                  {l.hr_note && (
                                    <div>
                                      <div className="text-xs text-muted-foreground">Additional Note:</div>
                                      <div className="italic">"{l.hr_note}"</div>
                                    </div>
                                  )}
                                  {l.reviewed_at && (
                                    <div className="text-xs text-muted-foreground">
                                      Rejected on: {formatIst(l.reviewed_at, "MMM d, yyyy")}
                                    </div>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        );
                      })() : l.hr_note ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-default">
                                {l.hr_note.length > 40 ? l.hr_note.slice(0, 40) + "…" : l.hr_note}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">{l.hr_note}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
